import { ContentPackType, Prompt } from 'lib/types/generated-types'
import { useContentPackType } from 'hooks/content_pack_types'

export const PromptsList = ({
  prompts,
  contentPackType,
  onPromptClick,
}: {
  prompts: Prompt[]
  contentPackType: ContentPackType
  onPromptClick?: (promptId: number) => void
}) => {
  const { data: dbContentPackType } = useContentPackType({
    typeId: contentPackType.id,
  })

  if (!dbContentPackType) {
    return <PromptsListSkeleton />
  }

  return (
    <div className="flex w-full max-w-xl flex-col justify-center gap-y-3 rounded-md">
      <div className="flex w-full flex-col">
        <div className="grid w-full grid-cols-1 gap-4 px-2 py-2 sm:grid-cols-2">
          {prompts?.map((prompt, index) => {
            return (
              <PromptItem
                key={prompt.id}
                prompt={prompt}
                onClick={() => onPromptClick?.(prompt.id)}
                index={index}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}

const PromptsListSkeleton = () => {
  return (
    <div className="flex w-full min-w-full max-w-xl flex-col justify-center gap-y-3 rounded-md">
      <div className="-ml-1 flex w-full flex-col">
        <div className="flex w-full items-center justify-between rounded-md">
          <div className="w-full">
            <div className="h-8 w-1/3 animate-pulse rounded-md bg-gray-200"></div>
            <div className="mt-2 h-4 w-2/3 animate-pulse rounded-md bg-gray-200"></div>
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <div className="flex w-full flex-col gap-y-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="flex w-full flex-col gap-y-2 rounded-md p-4"
            >
              <div className="flex w-full items-center justify-between gap-1">
                <div className="flex items-center gap-x-2">
                  <div className="h-5 w-5 animate-pulse rounded-md bg-gray-200"></div>
                  <div className="h-5 w-32 animate-pulse rounded-md bg-gray-200"></div>
                </div>
                <div className="h-5 w-20 animate-pulse rounded-md bg-gray-200"></div>
              </div>
              <div className="h-20 w-full animate-pulse rounded-md bg-gray-200"></div>
            </div>
          ))}
        </div>
        <div className="flex w-full flex-col gap-y-2">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="flex w-full flex-col gap-y-2 rounded-md p-4"
            >
              <div className="flex w-full items-center justify-between gap-1">
                <div className="flex items-center gap-x-2">
                  <div className="h-5 w-5 animate-pulse rounded-md bg-gray-200"></div>
                  <div className="h-5 w-32 animate-pulse rounded-md bg-gray-200"></div>
                </div>
                <div className="h-5 w-20 animate-pulse rounded-md bg-gray-200"></div>
              </div>
              <div className="h-20 w-full animate-pulse rounded-md bg-gray-200"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const PromptItem = ({
  prompt,
  onClick,
  index,
}: {
  prompt: Prompt
  onClick?: () => void
  index: number
}) => {
  // Calculate row number (integer division by 2 since we have 2 columns)
  const rowIndex = Math.floor(index / 2)

  return (
    <div
      className={`flex w-full cursor-pointer flex-col rounded-md ${
        rowIndex % 2 === 0
          ? 'bg-slate-50 hover:bg-slate-100'
          : 'bg-white hover:bg-slate-100'
      } p-4 transition-colors duration-200 ease-in-out`}
      onClick={onClick}
    >
      <div className="flex w-full items-center justify-between">
        <span className="text-base font-medium">{prompt.name}</span>
      </div>
      {prompt.prompt_text && (
        <p className={`mt-2 text-xs prose-p:text-gray-600`}>
          {prompt.prompt_text}
        </p>
      )}
    </div>
  )
}
